import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getContactFullLabel } from '../../utils/contactIdentity';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MobileSearchOverlay
 * ────────────────────
 * Recherche globale plein écran (mobile-only).
 *
 * - Réutilise le MÊME endpoint que le dropdown de recherche du header desktop
 *   (`GET /api/search?q=…&limit=8`) → parité fonctionnelle stricte.
 * - Debounce à 280 ms + AbortController pour ne pas empiler les requêtes.
 * - Focus automatique sur le champ à l'ouverture.
 * - Un clic sur un résultat navigue vers la fiche (bien ou contact) et ferme
 *   l'overlay — aucun state global externe requis (composant autonome).
 * - Fermeture au clic sur la croix / touche Escape / après sélection d'un résultat.
 *
 * NOTE : ce composant est déclenché depuis l'onglet « Recherche » de la barre
 * du bas (BottomNavigation). Il n'y a PAS de route dédiée `/search` — c'est un
 * overlay UI, ce qui évite de dupliquer une page pour un simple pattern de
 * recherche live.
 */
const MobileSearchOverlay = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  // Focus dès l'ouverture (contrat produit : « champ focus dès l'ouverture »).
  useEffect(() => {
    if (open && inputRef.current) {
      // rAF pour laisser le temps au portal de monter, sinon focus perdu sur iOS.
      const id = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    if (!open) {
      // Reset quand on ferme
      setQuery('');
      setResults(null);
      setLoading(false);
      if (abortRef.current) abortRef.current.abort();
    }
  }, [open]);

  // Empêche le scroll de la page derrière l'overlay
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Fermeture au Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const runSearch = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/search`, {
        params: { q, limit: 8 },
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      setResults(data);
    } catch (e) {
      if (!axios.isCancel(e) && e.name !== 'CanceledError') {
        setResults({ properties: [], contacts: [], total: 0 });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 2) {
      setResults(null);
      return;
    }
    debounceRef.current = setTimeout(() => runSearch(query), 280);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, runSearch]);

  const pickResult = (item) => {
    onClose?.();
    if (item.type === 'property') navigate(`/properties/${item.id}/view`);
    else if (item.type === 'contact') navigate(`/contacts/${item.id}`);
  };

  if (!open) return null;

  const props = results?.properties || [];
  const contacts = results?.contacts || [];
  const hasResults = props.length + contacts.length > 0;
  const showEmpty = query.length >= 2 && !loading && !hasResults;

  return (
    <div
      className="md:hidden fixed inset-0 bg-white z-[60] flex flex-col"
      data-testid="mobile-search-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Recherche globale"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      {/* Barre supérieure : champ + bouton fermer */}
      <div className="flex items-center gap-2 px-3 py-3 border-b border-slate-200">
        <div className="relative flex-1">
          <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            ref={inputRef}
            type="text"
            inputMode="search"
            enterKeyHint="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un bien, un contact, une référence…"
            className="w-full pl-10 pr-9 py-3 text-base bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
            data-testid="mobile-search-input"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-600"
              aria-label="Effacer"
            >
              <i className="bi bi-x-circle-fill" />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="mobile-search-close"
          className="px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg"
        >
          Annuler
        </button>
      </div>

      {/* Zone de résultats */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="p-8 text-center text-sm text-slate-500">
            <i className="bi bi-arrow-clockwise animate-spin mr-2" />Recherche…
          </div>
        )}

        {!loading && query.length < 2 && (
          <div className="p-8 text-center text-sm text-slate-500">
            <i className="bi bi-search text-3xl text-slate-300 block mb-3" />
            Tapez au moins 2 caractères pour lancer la recherche.
          </div>
        )}

        {showEmpty && (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="mobile-search-empty">
            <i className="bi bi-emoji-frown text-3xl text-slate-300 block mb-3" />
            Aucun résultat pour « <span className="font-semibold text-slate-700">{query}</span> ».
          </div>
        )}

        {!loading && hasResults && (
          <div>
            {props.length > 0 && (
              <section className="pt-3 pb-1">
                <h3 className="px-4 pb-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Biens ({props.length})
                </h3>
                <ul>
                  {props.map((p) => (
                    <li key={`p-${p.id}`}>
                      <button
                        type="button"
                        onClick={() => pickResult({ ...p, type: 'property' })}
                        data-testid={`mobile-search-result-property-${p.id}`}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 active:bg-slate-100 transition"
                      >
                        <span className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                          <i className="bi bi-building text-blue-600" />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-semibold text-slate-900 truncate">
                            {p.titre || p.title || p.reference || 'Bien'}
                          </span>
                          <span className="block text-xs text-slate-500 truncate">
                            {[p.reference, p.ville].filter(Boolean).join(' • ')}
                          </span>
                        </span>
                        <i className="bi bi-chevron-right text-slate-300" />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {contacts.length > 0 && (
              <section className="pt-3 pb-1 border-t border-slate-100">
                <h3 className="px-4 pb-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Contacts ({contacts.length})
                </h3>
                <ul>
                  {contacts.map((c) => (
                    <li key={`c-${c.id}`}>
                      <button
                        type="button"
                        onClick={() => pickResult({ ...c, type: 'contact' })}
                        data-testid={`mobile-search-result-contact-${c.id}`}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 active:bg-slate-100 transition"
                      >
                        <span className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                          <i className="bi bi-person text-emerald-600" />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-semibold text-slate-900 truncate">
                            {getContactFullLabel(c) !== 'Sans nom' ? getContactFullLabel(c) : 'Contact'}
                          </span>
                          <span className="block text-xs text-slate-500 truncate">
                            {[c.email, c.telephone].filter(Boolean).join(' • ')}
                          </span>
                        </span>
                        <i className="bi bi-chevron-right text-slate-300" />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileSearchOverlay;
